import {
  DateTimeFormatter,
  FormatterWithPrecision,
  type StringFormatter,
  type TimeFrameFormatter,
} from '../../core/types/formatter.js';
import { ScaleTypesMap } from '../types/scale-types-map.js';
import type {
  AreaSeriesContent,
  DotSeriesContent,
  LineSeriesContent,
  XYChartExploreState,
} from '../types/state.js';
import {
  AnyXYChartScale,
  type AREA_HORIZONTAL,
  AREA_VARIANT,
  type AREA_VERTICAL,
  type AreaMetadata,
  type AreaSeriesWithDatapoints,
  type BAND_HORIZONTAL,
  type BAND_VERTICAL,
  BAR_VARIANT,
  type BarMetadata,
  type BarSeriesWithDatapoints,
  type BaseMetadata,
  DOT_VARIANT,
  type DotMetadata,
  DotSeriesWithDatapoints,
  LINE_VARIANT,
  type LineMetadata,
  type LineSeriesWithDatapoints,
  type MapDatapoints,
  RECT_VARIANT,
  type RectDatapointInternal,
  type RectMetadata,
  RectSeriesWithDatapoints,
  type XYChartAreaSeriesInternal,
  type XYChartBarSeriesInternal,
  XYChartDotSeriesInternal,
  type XYChartLineSeriesInternal,
  XYChartRectSeriesInternal,
  XYChartSeriesWithDatapoints,
  XYChartVariantsInternal,
  type XYValueAccessorDataTypes,
} from '../types/xy-chart-internals.js';
import {
  type AreaDatapoint,
  type BarDatapoint,
  type DotDatapoint,
  type LineDatapoint,
  type RectDatapoint,
  XYAccessorDataTypes,
} from '../types/xy-chart.js';

interface BaseGuard {
  v0: XYAccessorDataTypes;
}

function isNumerical<T extends BaseGuard>(args: T): args is T {
  return typeof args.v0 === 'number';
}

function isCategorical<T extends BaseGuard>(args: T): args is T {
  return typeof args.v0 === 'string';
}

function isTime<T extends BaseGuard>(args: T): args is T {
  return args.v0 instanceof Date;
}

interface HoveringPointGuard extends BaseGuard {
  scale: AnyXYChartScale;
}

export function isNumericalHoveringPoint(
  args: HoveringPointGuard,
): args is ScaleTypesMap['numerical.linear']['hoveringPoint'] {
  return isNumerical(args);
}

export function isTimeHoveringPoint(
  args: HoveringPointGuard,
): args is ScaleTypesMap['time']['hoveringPoint'] {
  return isTime(args);
}

export function isCategoricalHoveringPoint(
  args: HoveringPointGuard,
): args is ScaleTypesMap['categorical']['hoveringPoint'] {
  return isCategorical(args);
}

interface DotSeriesScaleGuard extends BaseGuard {
  v1: Date | undefined;
  scale: AnyXYChartScale;
}

export function isNumericalDotScale(
  args: DotSeriesScaleGuard,
): args is ScaleTypesMap['numerical.linear']['dotScale'] {
  return isNumerical(args);
}

export function isTimeDotScale(
  args: DotSeriesScaleGuard,
): args is ScaleTypesMap['time']['dotScale'] {
  return isTime(args);
}

export function isCategoricalDotScale(
  args: DotSeriesScaleGuard,
): args is ScaleTypesMap['categorical']['dotScale'] {
  return isCategorical(args);
}

interface RectScaleGuard extends BaseGuard {
  scale: AnyXYChartScale;
  v1: XYAccessorDataTypes | undefined;
}
export function isNumericalRectScale(
  args: RectScaleGuard,
): args is ScaleTypesMap['numerical.linear']['rectScale'] {
  return isNumerical(args);
}

export function isTimeRectScale(
  args: RectScaleGuard,
): args is ScaleTypesMap['time']['rectScale'] {
  return isTime(args);
}

export function isCategoricalRectScale(
  args: RectScaleGuard,
): args is ScaleTypesMap['categorical']['rectScale'] {
  return isCategorical(args);
}

interface LineSeriesScaleGuard extends BaseGuard {
  v1: Date | undefined;
  scale: AnyXYChartScale;
}

export function isNumericalLineScale(
  args: LineSeriesScaleGuard,
): args is ScaleTypesMap['numerical.linear']['lineScale'] {
  return isNumerical(args);
}

export function isTimeLineScale(
  args: LineSeriesScaleGuard,
): args is ScaleTypesMap['time']['lineScale'] {
  return isTime(args);
}

export function isCategoricalLineScale(
  args: LineSeriesScaleGuard,
): args is ScaleTypesMap['categorical']['lineScale'] {
  return isCategorical(args);
}

interface BarSeriesScaleGuard {
  v0: XYAccessorDataTypes | undefined;
  v1: XYAccessorDataTypes | undefined;
  scale: AnyXYChartScale;
}

export function isNumericalBarScale(
  args: BarSeriesScaleGuard,
): args is ScaleTypesMap['numerical.linear']['barScale'] {
  return typeof args.v0 === 'number' || typeof args.v1 === 'number';
}

export function isTimeBarScale(
  args: BarSeriesScaleGuard,
): args is ScaleTypesMap['time']['barScale'] {
  return args.v0 instanceof Date || args.v1 instanceof Date;
}

export function isCategoricalBarScale(
  args: BarSeriesScaleGuard,
): args is ScaleTypesMap['categorical']['barScale'] {
  return typeof args.v0 === 'string' || typeof args.v1 === 'string';
}

interface AreaSeriesScaleGuard {
  v0: XYAccessorDataTypes | undefined;
  v1: XYAccessorDataTypes | undefined;
  scale: AnyXYChartScale;
}

export function isNumericalAreaScale(
  args: AreaSeriesScaleGuard,
): args is ScaleTypesMap['numerical.linear']['areaScale'] {
  return typeof args.v0 === 'number' || typeof args.v1 === 'number';
}

export function isTimeAreaScale(
  args: AreaSeriesScaleGuard,
): args is ScaleTypesMap['time']['areaScale'] {
  return args.v0 instanceof Date || args.v1 instanceof Date;
}

export function isCategoricalAreaScale(
  args: AreaSeriesScaleGuard,
): args is ScaleTypesMap['categorical']['areaScale'] {
  return typeof args.v0 === 'string' || typeof args.v1 === 'string';
}

export type TooltipFormatter =
  | FormatterWithPrecision
  | DateTimeFormatter
  | TimeFrameFormatter
  | StringFormatter
  | undefined;

export type TooltipGuard = BaseGuard &
  (
    | {
        formatter: TooltipFormatter;
      }
    | {
        formatter: TooltipFormatter;
        v1: XYAccessorDataTypes;
      }
  );

export function isNumericalToolTip<
  T1 extends TooltipGuard,
  T2 extends T1 & ScaleTypesMap['numerical.linear']['tooltip'],
>(args: T1): args is Pick<T2, keyof T1> {
  return isNumerical(args);
}

export function isTimeTooltip<
  T1 extends TooltipGuard,
  T2 extends T1 & ScaleTypesMap['time']['tooltip'],
>(args: T1): args is Pick<T2, keyof T1> {
  return isTime(args);
}

export type TooltipContextHoveredPoints = {
  series: HoveredSeries;
  datapoint: HoveredDatapoint<HoveredSeries>;
  metadata: HoveredMetadata<HoveredSeries>;
};

type RectTooltipContext = {
  series: RectSeriesWithDatapoints;
  datapoint: RectDatapoint;
  metadata: RectMetadata;
};

type BarTooltipContext = {
  series: BarSeriesWithDatapoints;
  datapoint: BarDatapoint;
  metadata: BarMetadata;
};

export type TooltipContextClosestPoints = {
  series: ClosestSeries;
  datapoint: ClosestDatapoint<ClosestSeries>;
  metadata: ClosestMetadata<ClosestSeries>;
};

type AreaTooltipContext = {
  series: AreaSeriesWithDatapoints;
  datapoint: AreaDatapoint;
  metadata: AreaMetadata;
};

export function isCategoricalTooltip<
  T1 extends TooltipGuard,
  T2 extends T1 & ScaleTypesMap['categorical']['tooltip'],
>(args: T1): args is Pick<T2, keyof T1> {
  return isCategorical(args);
}

export function isRectSeries(
  series: XYChartVariantsInternal,
): series is XYChartRectSeriesInternal;

export function isRectSeries(
  series: XYChartSeriesWithDatapoints,
): series is RectSeriesWithDatapoints;

export function isRectSeries(
  series: XYChartVariantsInternal | XYChartSeriesWithDatapoints,
): series is XYChartRectSeriesInternal | RectSeriesWithDatapoints {
  return series.type === RECT_VARIANT;
}

export function isDotSeries(
  series: XYChartVariantsInternal,
): series is XYChartDotSeriesInternal;

export function isDotSeries(
  series: XYChartSeriesWithDatapoints,
): series is DotSeriesWithDatapoints;

export function isDotSeries(
  series: XYChartVariantsInternal | XYChartSeriesWithDatapoints,
): series is XYChartDotSeriesInternal | DotSeriesWithDatapoints {
  return series.type === DOT_VARIANT;
}

export function isLineSeries(
  series: XYChartVariantsInternal,
): series is XYChartLineSeriesInternal;

export function isLineSeries(
  series: XYChartSeriesWithDatapoints,
): series is LineSeriesWithDatapoints;

export function isLineSeries(
  series: XYChartVariantsInternal | XYChartSeriesWithDatapoints,
): series is XYChartLineSeriesInternal | LineSeriesWithDatapoints {
  return series.type === LINE_VARIANT;
}

export function isBarSeries(
  series: XYChartVariantsInternal,
): series is XYChartBarSeriesInternal;

export function isBarSeries(
  series: XYChartSeriesWithDatapoints,
): series is BarSeriesWithDatapoints;

export function isBarSeries(
  series: XYChartVariantsInternal | XYChartSeriesWithDatapoints,
): series is XYChartBarSeriesInternal | BarSeriesWithDatapoints {
  return series.type === BAR_VARIANT;
}

export function isAreaSeries(
  series: XYChartVariantsInternal,
): series is XYChartAreaSeriesInternal;

export function isAreaSeries(
  series: XYChartSeriesWithDatapoints,
): series is AreaSeriesWithDatapoints;

export function isAreaSeries(
  series: XYChartVariantsInternal | XYChartSeriesWithDatapoints,
): series is XYChartAreaSeriesInternal | AreaSeriesWithDatapoints {
  return series.type === AREA_VARIANT;
}

export function isRectTooltipContext(
  context: TooltipContextHoveredPoints,
): context is RectTooltipContext {
  return context.series.type === 'rect' && !!context.metadata;
}

export function isBarTooltipContext(
  context: TooltipContextHoveredPoints,
): context is BarTooltipContext {
  return context.series.type === 'bar' && !!context.metadata;
}

export function isAreaTooltipContext(
  context: TooltipContextClosestPoints,
): context is AreaTooltipContext {
  return context.series.type === 'area' && !!context.metadata;
}

export function isDotTooltipContent(
  content: XYChartExploreState['content'],
): content is DotSeriesContent {
  return content?.series?.type === DOT_VARIANT;
}

export function isLineTooltipContent(
  content: XYChartExploreState['content'],
): content is LineSeriesContent {
  return content?.series?.type === LINE_VARIANT;
}

export function isAreaTooltipContent(
  content: XYChartExploreState['content'],
): content is AreaSeriesContent {
  return content?.series?.type === AREA_VARIANT;
}

export function isRectDatapoint(
  datapoint:
    | RectDatapointInternal
    | DotDatapoint
    | LineDatapoint
    | BarDatapoint,
): datapoint is RectDatapointInternal {
  return 'value' in datapoint;
}

export const isStringOrValidNumber = (
  value: XYValueAccessorDataTypes,
): value is string | number => {
  return (
    typeof value === 'string' ||
    (typeof value === 'number' && !Number.isNaN(value))
  );
};

export function isValidName(
  value: XYAccessorDataTypes | boolean | null | undefined,
): value is XYAccessorDataTypes | boolean {
  return value !== null && value !== undefined && !Number.isNaN(value);
}

// Hovered series types (bar and rect)
export type HoveredSeries = BarSeriesWithDatapoints | RectSeriesWithDatapoints;

export type HoveredDatapoint<T extends HoveredSeries> =
  T extends BarSeriesWithDatapoints ? BarDatapoint : RectDatapointInternal;

export type HoveredMetadata<T extends HoveredSeries> =
  T extends BarSeriesWithDatapoints
    ? BaseMetadata & Partial<BarMetadata>
    : BaseMetadata & Partial<RectMetadata>;

export type HoveredSeriesToMetadata<T extends HoveredSeries> = Map<
  T,
  HoveredMetadata<T>
>;

export type ClosestSeries =
  | DotSeriesWithDatapoints
  | LineSeriesWithDatapoints
  | AreaSeriesWithDatapoints;

export type ClosestDatapoint<T extends ClosestSeries> =
  T extends DotSeriesWithDatapoints
    ? DotDatapoint
    : T extends LineSeriesWithDatapoints
    ? LineDatapoint
    : AreaDatapoint;

export type ClosestMetadata<T extends ClosestSeries> =
  T extends DotSeriesWithDatapoints
    ? BaseMetadata & Partial<DotMetadata>
    : T extends LineSeriesWithDatapoints
    ? BaseMetadata & Partial<LineMetadata>
    : BaseMetadata & Partial<AreaMetadata>;

export type ClosestSeriesToMetadata<T extends ClosestSeries> = Map<
  T,
  ClosestMetadata<T>
>;

export type TwoParameterAccessor<
  T extends DotDatapoint | LineDatapoint | AreaDatapoint,
> = {
  x: (
    scale: AnyXYChartScale,
    relAccessor?: (value: number) => number,
  ) => (datapoint: T) => number;
  y: (
    scale: AnyXYChartScale,
    relAccessor?: (value: number) => number,
  ) => (datapoint: T) => number;
};

export type XFourParameterAccessor = {
  x: (
    scale: AnyXYChartScale,
    datapointToMetadata: MapDatapoints,
    relAccessor?: (value: number) => number,
    isSeriesStacked?: boolean,
  ) => (datapoint: AreaDatapoint) => number;
  y: (
    scale: AnyXYChartScale,
    relAccessor?: (value: number) => number,
  ) => (datapoint: AreaDatapoint) => number;
};

export type YFourParameterAccessor = {
  x: (
    scale: AnyXYChartScale,
    relAccessor?: (value: number) => number,
  ) => (datapoint: AreaDatapoint) => number;
  y: (
    scale: AnyXYChartScale,
    datapointToMetadata: MapDatapoints,
    relAccessor?: (value: number) => number,
    isSeriesStacked?: boolean,
  ) => (datapoint: AreaDatapoint) => number;
};

export type LinearAccessors = {
  [BAND_HORIZONTAL]: TwoParameterAccessor<AreaDatapoint>;
  [BAND_VERTICAL]: TwoParameterAccessor<AreaDatapoint>;
  [DOT_VARIANT]: TwoParameterAccessor<DotDatapoint>;
  [LINE_VARIANT]: TwoParameterAccessor<LineDatapoint>;
  [AREA_HORIZONTAL]: XFourParameterAccessor;
  [AREA_VERTICAL]: YFourParameterAccessor;
};

export function isHorizontalAreaAccessors(
  accessors: LinearAccessors[keyof LinearAccessors],
): accessors is XFourParameterAccessor {
  // The horizontal area accessors have 4 parameters (scale, datapointToMetadata, relAccessor, isSeriesStacked) while the others have only 2.
  return accessors.x.length === 4;
}

export function isVerticalAreaAccessors(
  accessors: LinearAccessors[keyof LinearAccessors],
): accessors is YFourParameterAccessor {
  // The vertical area accessors have 4 parameters (scale, datapointToMetadata, relAccessor, isSeriesStacked) while the others have only 2.
  return accessors.y.length === 4;
}
